/**
 * usePriceChangeGuard — customer-approved-line price-change re-approval gate.
 *
 * Lifted from WorkOrderLineItemForm.tsx:
 *  - handleSubmit's gate condition (~414-424): fires when the line already
 *    exists, is already customer-approved, and the total moved by more than
 *    a cent — so an edit to an approved line can't silently drift the price
 *    the customer signed off on.
 *  - handlePriceChangeConfirm's resolution branches (~439-458): 'resend'
 *    resets the line back to pending so the customer re-approves the new
 *    price; 'in_person' records an in-person re-approval by staff; 'skip'
 *    saves the new price without re-approval (no-op here — save happens
 *    via the caller's own save path, e.g. useWorkOrderLineSave).
 */

import api from '../services/api';
import { WorkOrderLineItem } from '../types';

export type ReapprovalChoice = 'resend' | 'in_person' | 'skip';

export interface GuardCheckArgs {
  lineItem: WorkOrderLineItem; // existing line (has id + customer_approval_status)
  oldTotal: number;
  newTotal: number;
}

export function usePriceChangeGuard(): {
  needsReapproval: (a: GuardCheckArgs) => boolean;
  resolve: (
    workOrderId: number,
    lineId: number,
    choice: ReapprovalChoice,
    opts?: { staffName?: string; notes?: string }
  ) => Promise<void>;
} {
  const needsReapproval = ({ lineItem, oldTotal, newTotal }: GuardCheckArgs): boolean => {
    return (
      lineItem.id != null &&
      lineItem.customer_approval_status === 'approved' &&
      Math.abs(oldTotal - newTotal) > 0.01
    );
  };

  const resolve = async (
    workOrderId: number,
    lineId: number,
    choice: ReapprovalChoice,
    opts?: { staffName?: string; notes?: string }
  ): Promise<void> => {
    if (choice === 'resend') {
      await api.patch(`/work-orders/${workOrderId}/line-items/${lineId}/customer-status`, {
        status: 'pending',
      });
    } else if (choice === 'in_person') {
      await api.post(`/work-orders/${workOrderId}/line-items/${lineId}/approve-in-person`, {
        staff_name: opts?.staffName || 'Staff',
        notes: opts?.notes,
        confirmed: true,
      });
    }
    // 'skip' → no-op
  };

  return { needsReapproval, resolve };
}
